/**
 * @module services/loggerService
 * @description Structured logging service with scopes and colors
 *
 * Provides consistent logging throughout the application with:
 * - Scoped loggers for different modules
 * - Colored output in development
 * - Configurable log levels
 * - Structured log data
 *
 * @example
 * import { createLogger } from '@services/loggerService';
 *
 * const log = createLogger('api');
 * log.info('Fetching items', { count: 10 });
 * log.error('Request failed', { status: 404 });
 */

import { logging } from '@/config';

// =============================================================================
// TYPES
// =============================================================================

/** Log levels */
export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

/** Log scope (module name) */
export type LogScope =
  | 'api'
  | 'store'
  | 'ui'
  | 'storage'
  | 'i18n'
  | 'app'
  | 'auth'
  | 'item'
  | 'collection'
  | 'supabase'
  | 'init';

/** Structured log data */
export interface LogData {
  [key: string]: unknown;
}

/** Logger interface */
export interface Logger {
  error(message: string, data?: LogData): void;
  warn(message: string, data?: LogData): void;
  info(message: string, data?: LogData): void;
  debug(message: string, data?: LogData): void;
  /** Create a child logger with additional context */
  child(context: LogData): Logger;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
};

const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  error: '#ff6b6b',
  warn: '#ffc107',
  info: '#5c7cfa',
  debug: '#888888',
};

const SCOPE_COLORS: Record<string, string> = {
  api: '#5c7cfa',
  store: '#28a745',
  ui: '#ffc107',
  storage: '#17a2b8',
  i18n: '#e83e8c',
  app: '#6f42c1',
  auth: '#fd7e14',
  item: '#20c997',
  collection: '#6610f2',
  supabase: '#3ecf8e',
  init: '#9b59b6',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a log level should be displayed
 */
function shouldLog(level: LogLevel): boolean {
  if (!logging.verbose && level === 'debug') {
    return false;
  }
  return logging.levels.includes(level);
}

/**
 * Format timestamp for logs
 */
function getTimestamp(): string {
  return new Date().toISOString().split('T')[1].slice(0, -1);
}

/**
 * Get console method for log level
 */
/** Console method names that accept variadic arguments */
type ConsoleMethod = 'error' | 'warn' | 'info' | 'log';

/** Type-safe console method lookup */
const consoleMethods: Record<ConsoleMethod, (...args: unknown[]) => void> = {
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  info: console.info.bind(console),
  log: console.log.bind(console),
};

function getConsoleMethod(level: LogLevel): ConsoleMethod {
  switch (level) {
    case 'error':
      return 'error';
    case 'warn':
      return 'warn';
    case 'info':
      return 'info';
    case 'debug':
      return 'log';
    default:
      return 'log';
  }
}

/**
 * Format log message with colors (for browsers)
 */
function formatWithColors(
  level: LogLevel,
  scope: string,
  message: string,
  data?: LogData
): unknown[] {
  const timestamp = getTimestamp();
  const levelColor = LOG_LEVEL_COLORS[level];
  const scopeColor = SCOPE_COLORS[scope] || '#888888';

  const parts = [`%c${timestamp}`, `%c[${level.toUpperCase()}]`, `%c[${scope}]`, `%c${message}`];

  const styles = [
    'color: #888888',
    `color: ${levelColor}; font-weight: bold`,
    `color: ${scopeColor}; font-weight: bold`,
    'color: inherit',
  ];

  if (data && Object.keys(data).length > 0) {
    return [parts.join(' '), ...styles, data];
  }

  return [parts.join(' '), ...styles];
}

/**
 * Format log message without colors (for production)
 */
function formatPlain(level: LogLevel, scope: string, message: string, data?: LogData): unknown[] {
  const timestamp = getTimestamp();
  const formatted = `${timestamp} [${level.toUpperCase()}] [${scope}] ${message}`;

  if (data && Object.keys(data).length > 0) {
    return [formatted, data];
  }

  return [formatted];
}

// =============================================================================
// LOGGER IMPLEMENTATION
// =============================================================================

/**
 * Create a scoped logger
 */
function createScopedLogger(scope: LogScope, context: LogData = {}): Logger {
  const log = (level: LogLevel, message: string, data?: LogData): void => {
    if (!shouldLog(level)) return;

    const mergedData = { ...context, ...data };
    const logFn = consoleMethods[getConsoleMethod(level)];

    if (logging.useColors && typeof window !== 'undefined') {
      const args = formatWithColors(level, scope, message, mergedData);
      logFn(...args);
    } else {
      const args = formatPlain(level, scope, message, mergedData);
      logFn(...args);
    }
  };

  return {
    error: (message: string, data?: LogData) => log('error', message, data),
    warn: (message: string, data?: LogData) => log('warn', message, data),
    info: (message: string, data?: LogData) => log('info', message, data),
    debug: (message: string, data?: LogData) => log('debug', message, data),
    child: (childContext: LogData) => createScopedLogger(scope, { ...context, ...childContext }),
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

/**
 * Create a logger for a specific scope/module
 *
 * @example
 * const log = createLogger('api');
 * log.info('Request started', { url: '/items' });
 */
export function createLogger(scope: LogScope): Logger {
  return createScopedLogger(scope);
}

/** Pre-configured loggers for common scopes */
export const log = {
  api: createLogger('api'),
  store: createLogger('store'),
  ui: createLogger('ui'),
  storage: createLogger('storage'),
  i18n: createLogger('i18n'),
  app: createLogger('app'),
  auth: createLogger('auth'),
  item: createLogger('item'),
  collection: createLogger('collection'),
};

export default log;
